import React, { useState, Fragment } from "react";
import {
  Box,
  TextField,
  MenuItem,
  useTheme,
  Menu,
  Button,
  ListItemIcon,
  ListItemText,
  Typography,
  Switch,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import {
  SearchRounded,
  SortRounded,
  FilterList,
  CheckRounded,
  FilterAltRounded,
  FilterAltOutlined,
} from "@mui/icons-material";
import { sortOptions } from "./Users";
import { GradientIconLight } from "../../../../../components/GradientIcon";
import { accountStatus } from "./Results/Member/AccountStatusChip";
import { useThemeSettings } from "../../../../../theme/ThemeSettingsProvider";

const useStyles = makeStyles()((theme) => ({
  filtersContainer: {
    width: "100%",
    overflow: "auto",
    [theme.breakpoints.up(1050)]: {
      // width: 908,
      margin: "0 auto",
    },
    [theme.breakpoints.up("sm")]: {
      // paddingRight: "32px",
    },
  },
  sortIcon: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
  },
  activeViewIcon: {
    background: "#E8EAF6",
  },
  gradientText: {
    background: theme.palette.accentGradient,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  gradientTextLight: {
    background: theme.palette.light,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  filterButton: {
    background: theme.palette.background.light,
    color: theme.palette.action.active,
    boxShadow: "none",
    textTransform: "none",
    "&:hover": {
      boxShadow: "none",
      background: theme.palette.action.hover,
      color: theme.palette.primary.main,
    },
  },
  selectedFilterButton: {
    background: theme.palette.selection,
    color: theme.palette.text.primary,
  },
}));

function Filters({
  sortOption,
  onSortFiles,
  query,
  onSearchFiles,
  status,
  onStatusChange,
  plant,
  onPlantChange,
  line,
  onLineChange,
  onResetFilters,
  users,
}) {
  const { mode } = useThemeSettings();
  const isDarkMode = mode === "dark";
  const { classes } = useStyles();
  // Only show reset if any filter is active
  const isFilterActive =
    query || status !== "active" || plant !== "all" || line !== "all";
  const [showFilters, setShowFilters] = useState(false);

  return (
    <Box className={classes.filtersContainer}>
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
        gap={1.5}>
        <Box flex={1} display={"flex"} alignItems={"center"} gap={1.5}>
          <Button
            variant="contained"
            startIcon={
              showFilters ? (
                isDarkMode ? (
                  <FilterAltRounded
                    fontSize="small"
                    sx={{ color: (theme) => theme.palette.text.primary }}
                  />
                ) : (
                  <GradientIconLight
                    Icon={FilterAltRounded}
                    sx={{ fontSize: 20 }}
                  />
                )
              ) : (
                <FilterAltOutlined fontSize="small" />
              )
            }
            onClick={() => setShowFilters((v) => !v)}
            className={`${classes.filterButton} ${
              showFilters ? classes.selectedFilterButton : ""
            }`}>
            Filter
          </Button>
          <SortFilter
            sortOption={sortOption}
            onSortFiles={onSortFiles}
            isDarkMode={isDarkMode}
          />
          <SearchFilter query={query} onSearchFiles={onSearchFiles} />
        </Box>
        <HidePendingInvitationsFilter />
      </Box>

      {showFilters && (
        <Box display="flex" alignItems="center" gap={2} mt={3}>
          <PlantFilter
            users={users}
            plant={plant}
            onPlantChange={onPlantChange}
          />
          <LineFilter users={users} line={line} onLineChange={onLineChange} />
          <StatusFilter status={status} onStatusChange={onStatusChange} />
          {isFilterActive && (
            <Button variant="text" onClick={onResetFilters}>
              <span className={classes.gradientText}>Clear filters</span>
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

function HidePendingInvitationsFilter() {
  const theme = useTheme();
  return (
    <Box
      visibility={"hidden"}
      borderRadius={2}
      bgcolor={theme.palette.background.light}
      px={1}
      height={40}
      display={"flex"}
      alignItems={"center"}
      gap={1}>
      <Typography variant="body2">Hide Pending Invitations</Typography>
      <Switch />
    </Box>
  );
}

function PlantFilter({ users, plant, onPlantChange }) {
  const plants = [
    "all",
    ...new Set(
      users
        .map((user) => (user?.plant ? user.plant : null))
        .filter((plant) => plant && plant.trim() !== "")
    ),
  ];
  return (
    <TextField
      select
      slotProps={{
        input: {
          style: { borderRadius: 8 },
        },
      }}
      variant="outlined"
      label="Plant"
      size="small"
      value={plant}
      onChange={(e) => onPlantChange(e.target.value)}
      sx={{ minWidth: 160 }}>
      {plants.map((option) => {
        return (
          <MenuItem key={option} value={option}>
            {option.charAt(0).toUpperCase() + option.slice(1)}
          </MenuItem>
        );
      })}
    </TextField>
  );
}

function SearchFilter({ query, onSearchFiles }) {
  const theme = useTheme();

  const handleSearchChange = (e) => {
    const value = e.target.value;
    onSearchFiles(value);
  };

  return (
    <Box flex={1}>
      <TextField
        size="small"
        fullWidth
        variant="outlined"
        placeholder="Search by name, email..."
        value={query}
        onChange={handleSearchChange}
        slotProps={{
          input: {
            startAdornment: (
              <SearchRounded sx={{ color: theme.palette.action.active }} />
            ),
            style: {
              borderRadius: 8,
              //   maxWidth: 391,
            },
          },
        }}
      />
    </Box>
  );
}

function LineFilter({ users, line, onLineChange }) {
  const lines = [
    "all",
    ...new Set(
      users
        .map((user) => (user?.line ? user.line : null))
        .filter((line) => line && line.trim() !== "")
    ),
  ];
  return (
    <TextField
      select
      slotProps={{
        input: {
          style: { borderRadius: 8 },
        },
      }}
      variant="outlined"
      label="Line"
      size="small"
      value={line}
      onChange={(e) => onLineChange(e.target.value)}
      sx={{ minWidth: 160 }}>
      {lines.map((option) => {
        return (
          <MenuItem key={option} value={option}>
            {option.charAt(0).toUpperCase() + option.slice(1)}
          </MenuItem>
        );
      })}
    </TextField>
  );
}

function SortFilter({ sortOption, onSortFiles, isDarkMode }) {
  const theme = useTheme();
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSort = (option) => {
    onSortFiles(option);
    handleClose();
  };

  return (
    <Fragment>
      <Button
        variant="contained"
        onClick={handleClick}
        startIcon={
          open ? (
            isDarkMode ? (
              <SortRounded
                fontSize="small"
                sx={{ color: (theme) => theme.palette.text.primary }}
              />
            ) : (
              <GradientIconLight Icon={SortRounded} sx={{ fontSize: 20 }} />
            )
          ) : (
            <SortRounded fontSize="small" />
          )
        }
        className={`${classes.filterButton} ${
          open ? classes.selectedFilterButton : ""
        }`}>
        Sort
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: 3,
              boxShadow:
                "0px 11px 15px -7px rgba(0, 0, 0, 0.20), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)",
            },
          },
        }}>
        {sortOptions.map((option) => {
          const { value, label } = option;
          const isSelected = sortOption === value;
          return (
            <MenuItem
              key={value}
              value={value}
              sx={{
                background: isSelected ? theme.palette.selection : "initial",
              }}
              onClick={() => handleSort(value)}>
              <ListItemIcon>
                {isSelected &&
                  (isDarkMode ? (
                    <CheckRounded
                      sx={{ color: (theme) => theme.palette.text.primary }}
                    />
                  ) : (
                    <GradientIconLight Icon={CheckRounded} />
                  ))}
              </ListItemIcon>
              <ListItemText>{label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </Fragment>
  );
}

function StatusFilter({ status, onStatusChange }) {
  const accountStatusOptions = {
    ...accountStatus,
  };
  return (
    <TextField
      select
      slotProps={{
        input: {
          style: { borderRadius: 8 },
        },
      }}
      variant="outlined"
      label="Account Status"
      size="small"
      value={status}
      onChange={(e) => onStatusChange(e.target.value)}
      sx={{ minWidth: 160 }}>
      {Object.keys(accountStatusOptions).map((option) => {
        return (
          <MenuItem key={option} value={option}>
            {accountStatusOptions[option].label}
          </MenuItem>
        );
      })}
    </TextField>
  );
}

export default Filters;
